import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { Functionality } from "../constants/functionality.constants";
import { IGrafanaLoginUrl } from "../interfaces/grafana.interface";
import { ApiService } from "./api.service";
import { PermissionService } from "./permission.service";

@Injectable({
  providedIn: "root",
})
export class GrafanaService {
  constructor(
    private api: ApiService,
    private permissionService: PermissionService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}

  /**
   * Whether the user's role allows opening Grafana, i.e. seeing the analytics (see openGrafana()).
   * @return True if opening Grafana is allowed, false otherwise
   */
  public canOpenGrafana(): boolean {
    return this.permissionService.isRouteEnabled(Functionality.OPEN_GRAFANA, "POST", "/grafana/login-url");
  }

  /**
   * Get a URL logging the connected user into Grafana. It carries a login token: it must never be logged.
   * @return The Grafana login URL
   */
  public getLoginUrl(): Observable<IGrafanaLoginUrl> {
    return this.api.post("grafana/login-url", undefined) as Observable<IGrafanaLoginUrl>;
  }

  /**
   * Open Grafana in a new tab, already logged in. The tab is opened right away, while the click still
   * counts as a user action (browsers block a tab opened once the login URL arrives as a popup), then
   * sent to the login URL, or closed with an error message if the URL could not be obtained.
   * If the browser blocks the tab anyway, the user is asked to allow popups and no login URL is requested.
   */
  public openGrafana(): void {
    const grafanaTab = window.open("", "_blank");
    if (!grafanaTab) {
      this.snackBar.open(`❗ ${this.translateService.instant("navigation.analytics.popupBlocked")}`, "OK", {
        duration: 5000,
      });
      return;
    }
    // Same as 'noopener': Grafana must not get access to the app's window
    grafanaTab.opener = null;

    this.getLoginUrl().subscribe({
      next: (loginUrl) => {
        grafanaTab.location.href = loginUrl.url;
      },
      error: () => {
        grafanaTab.close();
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });
      },
    });
  }
}
